import { Builder } from "@builder.io/react";
import React, { useEffect, useRef, useState } from "react";
import stylesModal from "../../Modals/Modal/Modal.module.scss";
import styles from "./style.module.scss";
import useWindowSize from "@hooks/useWindowSize";
import BuilderStringInput from "@utils/BuilderInputs/BuilderStringInput";
import BuilderRichTextInput from "@utils/BuilderInputs/BuilderRichTextInput";
import BuilderBooleanInput from "@utils/BuilderInputs/BuilderBooleanInput";
import BuilderMediaItemInput from "@utils/BuilderInputs/BuilderMediaItemInput";
import { ImageMediaItemUrl } from "@types";

export type PricingPageToolTipsType = {
  enabled: boolean;
  featureNameCheckList: string;
  featureNameHeader: string;
  image: ImageMediaItemUrl;
  subCopy: string;
};

const PricingPageToolTips: React.FC<PricingPageToolTipsType> = ({
  enabled,
  featureNameCheckList,
  featureNameHeader,
  image,
  subCopy,
}) => {
  const [modalOpen, setModalOpen] = useState(false);

  const { width: windowWidth } = useWindowSize();

  const isMobile = windowWidth < 768;
  const isDesktop = windowWidth >= 1024;

  const modalContainerRef = useRef<HTMLElement>(null);
  const toolTipContentRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toolTipContentRef.current &&
        !toolTipContentRef.current.contains(event.target)
      ) {
        setModalOpen(false);
      }
    };

    const handleOutsideClick = (event: MouseEvent) => {
      if (
        modalContainerRef.current &&
        !modalContainerRef.current.contains(event.target) &&
        event?.target?.id !== "button-tooltip"
      ) {
        setModalOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("click", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("click", handleOutsideClick);
    };
  }, []);

  const modalStylesContainer = isMobile
    ? `${stylesModal.modalContainer} ${
        modalOpen ? stylesModal.modalContainerActive : ""
      }`
    : modalOpen
    ? styles.toolTipContainer
    : styles.toolTipContainerHidden;

  const modalStylesContent = isMobile
    ? `${stylesModal.modalContent}  ${
        modalOpen ? stylesModal.modalContentActive : ""
      }`
    : styles.toolTipContent;

  const isDisabled = !enabled && isDesktop;

  const icon = isDisabled ? "tooltip-disabled" : "tooltip-enabled";

  return (
    <div ref={toolTipContentRef} className={styles.containerToolTipContent}>
      <div
        className={`${styles.containerTooltip} ${
          isDisabled && styles.containerTooltipDisabled
        }`}
      >
        <button
          type="button"
          className={styles.buttonNoStyle}
          onClick={() => setModalOpen(true)}
          onMouseEnter={() => {
            if (isDesktop) {
              setModalOpen(true);
            }
          }}
          onMouseLeave={() => {
            if (isDesktop) {
              setModalOpen(false);
            }
          }}
        >
          <svg id="button-tooltip">
            <use href={`/icons/tooltip.svg#${icon}`} />
          </svg>
        </button>
        <p
          className={styles.featureNameCheckList}
          dangerouslySetInnerHTML={{ __html: featureNameCheckList }}
        />
      </div>
      <div data-testid="modal" className={modalStylesContainer}>
        <div className={`${styles.toolTipModal}  ${modalStylesContent}`}>
          <div ref={modalContainerRef}>
            <div className={styles.containerHeader}>
              {featureNameHeader && isMobile && (
                <div
                  className={styles.featureNameHeader}
                  dangerouslySetInnerHTML={{
                    __html: featureNameHeader,
                  }}
                />
              )}
              {isMobile && (
                <button
                  className={`${styles.buttonNoStyle} ${styles.buttonClose}`}
                  onClick={() => setModalOpen(false)}
                >
                  <img
                    src="/icons/close-icon.svg"
                    alt="Close Icon"
                    className={styles.iconClose}
                  />
                </button>
              )}
            </div>

            {image?.mediaItemUrl && (
              <img
                className={styles.imageTooltip}
                src={image?.mediaItemUrl}
                alt={image?.altText}
              />
            )}
            {subCopy && (
              <h3
                className={styles.subCopy}
                dangerouslySetInnerHTML={{ __html: subCopy }}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingPageToolTips;

export const RegisterPricingPageToolTips = () => {
  Builder.registerComponent(PricingPageToolTips, {
    name: "pricing-tooltip-2023",
    inputs: [
      new BuilderBooleanInput("enabled", {
        defaultValue: false,
      }),
      new BuilderRichTextInput("featureNameCheckList", {
        friendlyName: "Tooltip feature name in checklist",
        defaultValue: "Lorem ipsum dolor sit amet",
      }),
      new BuilderRichTextInput("featureNameHeader", {
        friendlyName: "Tooltip feature name in modal on mobile",
        defaultValue: "Lorem ipsum",
      }),
      new BuilderStringInput("subCopy", {
        friendlyName: "Tooltip subcopy",
        defaultValue: "Lorem ipsum dolor sit amet",
      }),
      new BuilderMediaItemInput("image", {
        friendlyName: "Tooltip image",
      }),
    ],
  });
};
